import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { navigate } from "../lib/router";
import TopBar from "../components/TopBar";

type RefType = "foreplay" | "url" | "upload";

const PLATFORMS = [
  { v: "meta_reels", l: "Meta Reels / Stories" },
  { v: "instagram", l: "Instagram Feed" },
  { v: "tiktok", l: "TikTok" },
];

export default function NewProjectWizard() {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [refType, setRefType] = useState<RefType>("upload");
  const [refValue, setRefValue] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [brief, setBrief] = useState("");
  const [language, setLanguage] = useState("uk");
  const [platform, setPlatform] = useState("meta_reels");
  const [cta, setCta] = useState("Take Quiz");

  const canNext1 = !!name.trim() && (refType === "upload" ? !!file : !!refValue.trim());

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      const project = await api.createProject({
        name,
        brief,
        language,
        platform,
        cta,
        refSourceType: refType,
        refSourceInput: refType === "upload" ? (file?.name ?? "") : refValue,
      } as never);
      if (refType === "upload" && file) {
        await api.uploadReference(project.id, file);
      }
      await qc.invalidateQueries({ queryKey: ["projects"] });
      navigate(`/projects/${project.id}/analysis`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Помилка");
      setBusy(false);
    }
  }

  return (
    <>
      <TopBar crumbs={["Проєкти", "Новий проєкт"]} />
      <div className="content" style={{ maxWidth: 760 }}>
        <div className="page-head">
          <div>
            <h1>Новий відео-проєкт</h1>
            <div className="lead">Крок {step} з 3</div>
          </div>
          <button className="btn ghost" onClick={() => navigate("/projects")}>Скасувати</button>
        </div>

        <Stepper step={step} />

        {step === 1 && (
          <div className="card pad">
            <h2>Референс і назва</h2>
            <div className="field">
              <label>Назва проєкту</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="напр. Kidden — адаптація FMK (нічна сцена)" />
            </div>

            <div className="field">
              <label>Джерело референсу</label>
              <div className="filters" style={{ marginBottom: 10 }}>
                {(["foreplay", "url", "upload"] as RefType[]).map((t) => (
                  <div key={t} className={"filter" + (refType === t ? " active" : "")} onClick={() => setRefType(t)}>
                    {t === "foreplay" ? "Foreplay-лінк" : t === "url" ? "Відео-URL" : "Завантажити MP4"}
                  </div>
                ))}
              </div>
              {refType === "upload" ? (
                <>
                  <input
                    className="input"
                    type="file"
                    accept="video/mp4,video/quicktime,video/*"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                  {file && <div className="hint">Обрано: {file.name} ({Math.round(file.size / 1024 / 1024)} МБ)</div>}
                </>
              ) : (
                <input
                  className="input"
                  value={refValue}
                  onChange={(e) => setRefValue(e.target.value)}
                  placeholder={refType === "foreplay" ? "https://app.foreplay.co/share/…" : "https://…/video.mp4"}
                />
              )}
              <div className="hint">
                {refType === "foreplay"
                  ? "⚠ Foreplay share-лінки публічний API не резолвить (потрібен числовий Meta ad_id). Найнадійніше — завантажити MP4 або вставити пряме відео-URL."
                  : refType === "upload"
                    ? "Завантаж MP4/MOV референсу (до 200 МБ) — найуніверсальніший спосіб."
                    : "Пряме URL на відеофайл (напр. r2.foreplay.co/...mp4 або fbcdn)."}
              </div>
            </div>
            <Nav onNext={() => setStep(2)} nextDisabled={!canNext1} />
          </div>
        )}

        {step === 2 && (
          <div className="card pad">
            <h2>Технічне завдання (ТЗ)</h2>
            <div className="field">
              <label>Бриф маркетолога</label>
              <textarea
                className="textarea"
                style={{ minHeight: 160 }}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder={"Що адаптувати / що зберегти зі структури референсу. Напр.:\n• Повторити структуру 1-в-1, замінити бренд FMK → Kidden\n• Лайфстайл-сцени генерувати з нуля, app-екрани — у Figma/AE\n• Хук: \"Still fighting over screen time?\"\n• CTA: Take Quiz"}
              />
              <div className="hint">UA для нотаток, англомовні рядки бери в лапки — студія перенесе їх у промпти.</div>
            </div>
            <Nav onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </div>
        )}

        {step === 3 && (
          <div className="card pad">
            <h2>Бренд і ціль</h2>
            <div className="row">
              <div className="field">
                <label>Платформа</label>
                <select className="input" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                  {PLATFORMS.map((p) => (
                    <option key={p.v} value={p.v}>{p.l}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Мова виходу</label>
                <select className="input" value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="uk">UA нотатки / EN копі</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label>CTA</label>
              <input className="input" value={cta} onChange={(e) => setCta(e.target.value)} />
            </div>
            <div className="hint" style={{ marginBottom: 8 }}>
              Бренд-кіт Kidden (лого, палітра #316DFC/#FF6D00, шрифт) підставиться автоматично у бренд-чеклист.
            </div>
            {err && <div className="hint" style={{ color: "var(--red)" }}>{err}</div>}
            <Nav
              onBack={() => setStep(2)}
              nextLabel={busy ? "Створення…" : "Створити та проаналізувати"}
              onNext={create}
              nextDisabled={busy}
              primary
            />
          </div>
        )}
      </div>
    </>
  );
}

function Stepper({ step }: { step: number }) {
  const labels = ["Референс", "ТЗ", "Бренд і ціль"];
  return (
    <div className="tabs" style={{ marginBottom: 18 }}>
      {labels.map((l, i) => (
        <div key={i} className={"tab" + (step === i + 1 ? " active" : "")}>{i + 1}. {l}</div>
      ))}
    </div>
  );
}

function Nav({
  onBack,
  onNext,
  nextDisabled,
  nextLabel = "Далі",
  primary,
}: {
  onBack?: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  primary?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      {onBack ? <button className="btn" onClick={onBack}>Назад</button> : <span />}
      <button className={"btn " + (primary ? "primary" : "primary")} onClick={onNext} disabled={nextDisabled}>
        {nextLabel}
      </button>
    </div>
  );
}
